import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';

// Control debug logging - set to false in production
const DEBUG = true; // Temporarily enabling for troubleshooting

// Instead of creating the client immediately at the module level,
// create a function that ensures the client is generated only when needed
let apiClient = null;

function getClient() {
  if (!apiClient) {
    apiClient = generateClient();
  }
  return apiClient;
}
// Get user's subscription status
export const getUserSubscriptionStatus = async () => {
  try {
    const recordResult = await getPrimaryUserRecord();
    
    if (recordResult.success && recordResult.record) {
      // Return the subscription status from the record
      return {
        success: true,
        subscriptionStatus: recordResult.record.subscriptionStatus || 'freetrial', // Default to freetrial if not set
        userId: recordResult.userId,
        userEmail: recordResult.userEmail
      };
    } else {
      // No record found, user would be considered a free trial when record is created
      return {
        success: true,
        subscriptionStatus: 'freetrial', 
        userId: recordResult.userId,
        userEmail: recordResult.userEmail,
        recordExists: false
      };
    }
  } catch (error) {
    if (DEBUG) {
      console.error('Error getting user subscription status:', error);
    }
    
    return {
      success: false,
      error,
      message: 'Error retrieving subscription status'
    };
  }
};

// GraphQL operations
const createUserWidgetSettingsMutation = /* GraphQL */ `
  mutation CreateUserWidgetSettings($input: CreateUserWidgetSettingsInput!) {
    createUserWidgetSettings(input: $input) {
      id
      userId
      userEmail
      settings
      updatedAt
      subscriptionStatus
    }
  }
`;

const updateUserWidgetSettingsMutation = /* GraphQL */ `
  mutation UpdateUserWidgetSettings($input: UpdateUserWidgetSettingsInput!) {
    updateUserWidgetSettings(input: $input) {
      id
      userId
      userEmail
      settings
      updatedAt
      subscriptionStatus
    }
  }
`;

const deleteUserWidgetSettingsMutation = /* GraphQL */ `
  mutation DeleteUserWidgetSettings($input: DeleteUserWidgetSettingsInput!) {
    deleteUserWidgetSettings(input: $input) {
      id
      userId
      userEmail
    }
  }
`;

// Modified to retrieve all records for the user
const getUserWidgetSettingsQuery = /* GraphQL */ `
  query GetUserWidgetSettings($userId: ID!) {
    listUserWidgetSettings(filter: {userId: {eq: $userId}}) {
      items {
        id
        userId
        userEmail
        settings
        updatedAt
        subscriptionStatus
      }
    }
  }
`;

// Modified to retrieve all records for the email
const getUserWidgetSettingsByEmailQuery = /* GraphQL */ `
  query GetUserWidgetSettingsByEmail($userEmail: String!) {
    listUserWidgetSettings(filter: {userEmail: {eq: $userEmail}}) {
      items {
        id
        userId
        userEmail
        settings
        updatedAt
        subscriptionStatus
      }
    }
  }
`;

// Handle duplicate records by selecting the most recently updated one and cleaning up others
const handlePotentialDuplicates = async (items, client, userId, userEmail) => {
  if (!items || items.length <= 1) return items;
  
  // Sort by updatedAt descending (newest first)
  const sortedItems = [...items].sort((a, b) => 
    new Date(b.updatedAt) - new Date(a.updatedAt));
  
  if (DEBUG) {
    console.log(`Found ${items.length} records for user, consolidating to most recent: ${sortedItems[0].id}`);
  }
  
  // The first record is the one we'll keep
  const primaryRecord = sortedItems[0];
  
  // If there are multiple records, consolidate them
  if (sortedItems.length > 1) {
    try {
      // Process records that need to be merged/deleted
      for (let i = 1; i < sortedItems.length; i++) {
        const recordToDelete = sortedItems[i];
        
        // Attempt to merge data before deleting (optional)
        try {
          const recordToDeleteSettings = JSON.parse(recordToDelete.settings);
          const primarySettings = JSON.parse(primaryRecord.settings);
          
          // If the main record is using the new format but the to-be-deleted record 
          // has data in slots that the main record doesn't have, merge them
          if (primarySettings.save_slots && recordToDeleteSettings.save_slots) {
            let dataChanged = false;
            
            recordToDeleteSettings.save_slots.forEach((slot, index) => {
              if (slot.settings && !primarySettings.save_slots[index].settings) {
                primarySettings.save_slots[index] = slot;
                dataChanged = true;
              }
            });
            
            // If we merged any data, update the primary record
            if (dataChanged) {
              await client.graphql({
                query: updateUserWidgetSettingsMutation,
                variables: {
                  input: {
                    id: primaryRecord.id,
                    userId: userId,
                    userEmail: userEmail,
                    settings: JSON.stringify(primarySettings),
                    updatedAt: new Date().toISOString()
                  }
                }
              });
              
              if (DEBUG) {
                console.log(`Updated primary record ${primaryRecord.id} with merged data`);
              }
            }
          }
        } catch (mergeError) {
          if (DEBUG) {
            console.error('Error merging settings before deletion:', mergeError);
          }
        }
        
        // Delete the duplicate record
        await client.graphql({
          query: deleteUserWidgetSettingsMutation,
          variables: {
            input: {
              id: recordToDelete.id
            }
          }
        });
        
        if (DEBUG) {
          console.log(`Deleted duplicate record: ${recordToDelete.id}`);
        }
      }
    } catch (cleanupError) {
      if (DEBUG) {
        console.error('Error cleaning up duplicate records:', cleanupError);
      }
      // Continue with the primary record even if cleanup fails
    }
  }
  
  return [primaryRecord];
};

// Update the userId of a record when it differs from the current one
const syncUserId = async (client, item, currentUserId) => {
  if (!item || item.userId === currentUserId) return;
  
  try {
    await client.graphql({
      query: updateUserWidgetSettingsMutation,
      variables: {
        input: {
          id: item.id,
          userId: currentUserId,
          updatedAt: new Date().toISOString()
        }
      }
    });
    if (DEBUG) {
      console.log(`Updated userId from ${item.userId} to ${currentUserId}`);
    }
  } catch (updateError) {
    if (DEBUG) {
      console.error('Error updating userId:', updateError);
    }
  }
};

// Initialize empty save slots structure
const initializeSaveSlots = () => {
  return {
    save_slots: Array(5).fill().map((_, i) => ({
      slot: i + 1,
      name: `Save ${i + 1}`,
      settings: null,
      active: i === 0, // First slot is active by default
      last_saved: null
    }))
  };
};
export const checkAuthStatus = async () => {
  try {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();
    return {
      authenticated: true,
      userId: user.userId,
      email: attributes.email || 'no-email-provided',
      message: "User is authenticated"
    };
  } catch (error) {
    console.log("Auth status check error:", error);
    return {
      authenticated: false,
      error,
      message: error.message || "User is not authenticated"
    };
  }
};

// Get primary user record, consolidating duplicates if needed
export const getPrimaryUserRecord = async () => {
  try {
    // First check if user is authenticated
    const authStatus = await checkAuthStatus();
    
    if (!authStatus.authenticated) {
      console.error("Authentication required:", authStatus.message);
      return {
        success: false,
        error: authStatus.error,
        message: "Authentication required",
        authError: true // Flag specifically as auth error
      };
    }
    
    const client = getClient();
    const userId = authStatus.userId;
    const userEmail = authStatus.email;

    try {
      // First try to find by email (more reliable)
      const existingSettingsByEmailResponse = await client.graphql({
        query: getUserWidgetSettingsByEmailQuery,
        variables: { userEmail }
      });

      let existingSettingsItems = existingSettingsByEmailResponse.data.listUserWidgetSettings.items;

      // If not found by email, fall back to userId
      if (!existingSettingsItems || existingSettingsItems.length === 0) {
        const existingSettingsByIdResponse = await client.graphql({
          query: getUserWidgetSettingsQuery,
          variables: { userId }
        });
        existingSettingsItems = existingSettingsByIdResponse.data.listUserWidgetSettings.items;
      } 
      
      // Handle potential duplicates - this now also consolidates records
      if (existingSettingsItems && existingSettingsItems.length > 0) {
        existingSettingsItems = await handlePotentialDuplicates(existingSettingsItems, client, userId, userEmail);
        
        // Update userId if it's different from current
        if (existingSettingsItems.length > 0 && existingSettingsItems[0].userId !== userId) {
          await syncUserId(client, existingSettingsItems[0], userId);
        }
        
        return {
          success: true,
          record: existingSettingsItems[0],
          userId,
          userEmail
        };
      }
      
      return {
        success: false,
        userId,
        userEmail,
        message: 'No existing record found'
      };
    } catch (graphqlError) {
      // Log detailed GraphQL error information
      if (DEBUG) {
        console.error('GraphQL operation failed:', graphqlError);
        if (graphqlError.errors) {
          console.error('GraphQL errors:', graphqlError.errors);
          graphqlError.errors.forEach((err, i) => {
            console.error(`Error ${i + 1}:`, err.message);
          });
        }
      }
      throw graphqlError; // Re-throw to be caught by outer catch
    }
  } catch (error) {
    if (DEBUG) {
      console.error('Error getting primary user record:', error);
      
      // Extract more details from the error if possible
      if (error.errors && Array.isArray(error.errors)) {
        error.errors.forEach(err => {
          console.error('GraphQL error detail:', err);
        });
      }
    }
    return {
      success: false,
      error,
      message: error.message || 'Error retrieving user records'
    };
  }
};

// Save settings to a specific slot
export const saveSettingsToSlot = async (settings, slotNumber, slotName, setAsActive = false) => {
  try {
    const client = getClient();
    
    // Get the primary record (or user info if no record exists)
    const recordResult = await getPrimaryUserRecord();
    const userId = recordResult.userId;
    const userEmail = recordResult.userEmail;
    
    const currentTimestamp = new Date().toISOString();
    
    let saveSlotsData;
    let settingsId;
    let currentActiveSlot = null;
    
    // Initialize or retrieve save slots structure
    if (recordResult.success && recordResult.record) {
      settingsId = recordResult.record.id;
      
      try {
        saveSlotsData = JSON.parse(recordResult.record.settings);
        
        // If old format (single settings object), convert to slots format
        if (!saveSlotsData.save_slots) {
          saveSlotsData = initializeSaveSlots();
          saveSlotsData.save_slots[0].settings = recordResult.record.settings;
          saveSlotsData.save_slots[0].last_saved = recordResult.record.updatedAt;
        }
        
        // Find which slot is currently active
        const activeSlotIndex = saveSlotsData.save_slots.findIndex(slot => slot.active);
        if (activeSlotIndex >= 0) {
          currentActiveSlot = activeSlotIndex + 1;
        }
      } catch (e) {
        if (DEBUG) {
          console.error(`Error parsing settings for ID ${recordResult.record.id}: ${e.message}`);
        }
        saveSlotsData = initializeSaveSlots();
      }
    } else {
      saveSlotsData = initializeSaveSlots();
    }
    
    // Validate slot number
    const slotIndex = slotNumber - 1;
    if (slotIndex < 0 || slotIndex >= 5) {
      throw new Error('Invalid slot number. Must be 1-5');
    }
    
    // Determine if we should set this slot as active
    const shouldBeActive = setAsActive || (currentActiveSlot === null);
    
    // Update slot with new settings
    saveSlotsData.save_slots[slotIndex] = {
      slot: slotNumber,
      name: slotName || saveSlotsData.save_slots[slotIndex].name,
      settings: settings,
      active: shouldBeActive,
      last_saved: currentTimestamp
    };
    
    // If setting as active, make sure all other slots are inactive
    if (shouldBeActive) {
      saveSlotsData.save_slots.forEach((slot, idx) => {
        if (idx !== slotIndex && slot.active) {
          slot.active = false;
        }
      });
    }
    
    // Save locally as active settings if this is becoming active
    if (shouldBeActive) {
      localStorage.setItem('settings', JSON.stringify(settings));
    }
    
    // Always save the slots structure locally
    localStorage.setItem('save_slots', JSON.stringify(saveSlotsData));
    
    // Update or create in database
    if (settingsId) {
      const updateResult = await client.graphql({
        query: updateUserWidgetSettingsMutation,
        variables: {
          input: {
            id: settingsId,
            userId, // Ensure userId is included and updated
            userEmail,
            settings: JSON.stringify(saveSlotsData),
            updatedAt: currentTimestamp
          }
        }
      });
      
      
      return {
        success: true,
        data: updateResult.data.updateUserWidgetSettings,
        message: shouldBeActive 
          ? `Settings saved to "${slotName}" and set as active`
          : `Settings saved to "${slotName || `Slot ${slotNumber}`}"`,
        slotData: saveSlotsData
      };
    } else {
      const createResult = await client.graphql({
        query: createUserWidgetSettingsMutation,
        variables: {
          input: {
            userId,
            userEmail,
            settings: JSON.stringify(saveSlotsData),
            subscriptionStatus: "freetrial", // Set new users to free trial status
            createdAt: currentTimestamp,
            updatedAt: currentTimestamp
          }
        }
      });
      
      return {
        success: true,
        data: createResult.data.createUserWidgetSettings,
        message: shouldBeActive 
          ? `Settings saved to "${slotName}" and set as active`
          : `Settings saved to "${slotName || `Slot ${slotNumber}`}"`,
        slotData: saveSlotsData
      };
    }
  } catch (error) {
    if (DEBUG) {
      console.error('Error saving to slot:', error);
    }
    
    return {
      success: false,
      error,
      message: 'Error saving settings to cloud'
    };
  }
};

// Set a slot as the active one without changing its content
export const setActiveSlot = async (slotNumber, updateRemote = true) => {
  try {
    // Get the primary record
    const recordResult = await getPrimaryUserRecord();
    
    if (!recordResult.success) {
      return {
        success: false,
        message: 'No saved settings found'
      };
    }
    
    const settingsId = recordResult.record.id;
    const userId = recordResult.userId;
    const userEmail = recordResult.userEmail;
    const client = getClient();
    
    // Parse saved slots
    let saveSlotsData;
    try {
      saveSlotsData = JSON.parse(recordResult.record.settings);
      
      // If old format, handle gracefully
      if (!saveSlotsData.save_slots) {
        saveSlotsData = initializeSaveSlots();
        saveSlotsData.save_slots[0].settings = JSON.parse(recordResult.record.settings);
      }
    } catch (e) {
      if (DEBUG) {
        console.error(`Error parsing settings for ID ${settingsId}: ${e.message}`);
      }
      return {
        success: false,
        message: 'Error parsing saved settings'
      };
    }
    
    // Validate slot number
    const slotIndex = slotNumber - 1;
    if (slotIndex < 0 || slotIndex >= 5 || !saveSlotsData.save_slots[slotIndex].settings) {
      return {
        success: false,
        message: 'Invalid or empty slot'
      };
    }
    
    // Set active slot
    saveSlotsData.save_slots.forEach((slot, idx) => {
      slot.active = (idx === slotIndex);
    });
    
    // Get the active settings from the slot
    const activeSettings = saveSlotsData.save_slots[slotIndex].settings;
    
    // Save locally
    localStorage.setItem('settings', JSON.stringify(activeSettings));
    localStorage.setItem('save_slots', JSON.stringify(saveSlotsData));
    
    // Update in database if updateRemote is true
    if (updateRemote) {
      try {
        const currentTimestamp = new Date().toISOString();
        
        // Update database with complete record details
        const updateResult = await client.graphql({
          query: updateUserWidgetSettingsMutation,
          variables: {
            input: {
              id: settingsId,
              userId: userId,
              userEmail: userEmail, 
              settings: JSON.stringify(saveSlotsData),
              updatedAt: currentTimestamp
            }
          }
        });
        
        if (!updateResult.data?.updateUserWidgetSettings) {
          return {
            success: false,
            message: 'Error updating database'
          };
        }
      } catch (updateError) {
        if (DEBUG) {
          console.error('Error updating database:', updateError);
        }
        return {
          success: false,
          error: updateError,
          message: 'Error updating active slot in database'
        };
      }
    }
    
    return {
      success: true,
      message: `Loaded settings from "${saveSlotsData.save_slots[slotIndex].name}"`,
      settings: activeSettings,
      slotData: saveSlotsData
    };
  } catch (error) {
    if (DEBUG) {
      console.error('Error setting active slot:', error);
    }
    
    return {
      success: false,
      error,
      message: 'Error loading settings'
    };
  }
};

export const loadSaveSlots = async () => {
  try {
    // Get the primary record
    const recordResult = await getPrimaryUserRecord();
    const client = getClient();
    
    if (recordResult.success && recordResult.record) {
      try {
        const saveSlotsData = JSON.parse(recordResult.record.settings);
        
        // Handle old format if needed
        if (!saveSlotsData.save_slots) {
          const newFormatData = initializeSaveSlots();
          newFormatData.save_slots[0].settings = JSON.parse(recordResult.record.settings);
          newFormatData.save_slots[0].last_saved = recordResult.record.updatedAt;
          
          // Save the converted format
          localStorage.setItem('save_slots', JSON.stringify(newFormatData));
          
          return {
            success: true,
            slotData: newFormatData
          };
        }
        
        // Save locally for offline use
        localStorage.setItem('save_slots', JSON.stringify(saveSlotsData));
        
        return {
          success: true,
          slotData: saveSlotsData
        };
      } catch (e) {
        if (DEBUG) {
          console.error(`Error parsing settings for ID ${recordResult.record.id}: ${e.message}`);
        }
        return {
          success: false,
          message: 'Error parsing saved data'
        };
      }
    }
    
    // Try local storage
    const localSaveSlots = localStorage.getItem('save_slots');
    if (localSaveSlots) {
      try {
        return {
          success: true,
          slotData: JSON.parse(localSaveSlots),
          source: 'local'
        };
      } catch (e) {
        if (DEBUG) {
          console.error('Error parsing local save slots:', e);
        }
      }
    }
    
    // No data found, return empty structure
    const emptySlots = initializeSaveSlots();
    localStorage.setItem('save_slots', JSON.stringify(emptySlots));
    
    return {
      success: true,
      slotData: emptySlots,
      source: 'initialized'
    };
  } catch (error) {
    if (DEBUG) {
      console.error('Error loading save slots:', error);
    }
    
    // Try local fallback
    try {
      const localSaveSlots = localStorage.getItem('save_slots');
      if (localSaveSlots) {
        return {
          success: true,
          slotData: JSON.parse(localSaveSlots),
          source: 'local-fallback'
        };
      }
    } catch (e) {
      if (DEBUG) {
        console.error('Error loading from localStorage:', e);
      }
    }
    
    // Last resort - return empty structure
    const emptySlots = initializeSaveSlots();
    return {
      success: false,
      slotData: emptySlots,
      error,
      message: 'Error loading saved settings'
    };
  }
};

// Verify if the current user matches the stored one and handle changes
export const verifyCurrentUser = async () => {
  try {
    const user = await getCurrentUser();
    const userId = user.userId;
    
    // Check if we have a stored user ID
    const storedUserId = localStorage.getItem('lastUserId');
    
    // First time user, just store the ID and return valid
    if (!storedUserId) {
      localStorage.setItem('lastUserId', userId);
      return { valid: true, changed: false };
    }
    
    // Check if user has changed
    const hasChanged = storedUserId !== userId;
    
    // Update stored user if changed
    if (hasChanged) {
      localStorage.setItem('lastUserId', userId);
    }
    
    return { valid: true, changed: hasChanged };
  } catch (error) {
    if (DEBUG) {
      console.error('Error verifying current user:', error);
    }
    return { valid: false, changed: false, error };
  }
};

// Load save slots with user verification
export const loadSaveSlotsSafe = async () => {
  try {
    const userStatus = await verifyCurrentUser();
    
    if (!userStatus.valid) {
      return {
        success: false,
        message: 'User session is invalid'
      };
    }
    
    return await loadSaveSlots();
  } catch (error) {
    if (DEBUG) {
      console.error('Error in loadSaveSlotsSafe:', error);
    }
    
    return {
      success: false,
      error,
      message: 'Error loading saved slots safely'
    };
  }
};

// Save settings to slot with user verification
export const saveSettingsToSlotSafe = async (settings, slotNumber, slotName, setAsActive = false) => {
  try {
    const userStatus = await verifyCurrentUser();
    
    if (!userStatus.valid) {
      return {
        success: false,
        message: 'User session is invalid'
      };
    }
    
    return await saveSettingsToSlot(settings, slotNumber, slotName, setAsActive);
  } catch (error) {
    if (DEBUG) {
      console.error('Error in saveSettingsToSlotSafe:', error);
    }
    
    return {
      success: false,
      error,
      message: 'Error saving settings safely'
    };
  }
};

// Set active slot with user verification
export const setActiveSlotSafe = async (slotNumber, updateRemote = true) => {
  try {
    const userStatus = await verifyCurrentUser();
    
    if (!userStatus.valid) {
      return {
        success: false,
        message: 'User session is invalid'
      };
    }
    
    return await setActiveSlot(slotNumber, updateRemote);
  } catch (error) {
    if (DEBUG) {
      console.error('Error in setActiveSlotSafe:', error);
    }
    
    return {
      success: false,
      error,
      message: 'Error setting active slot safely'
    };
  }
};

// Load active settings (for backwards compatibility)
export const loadUserSettings = async (defaultSettings) => {
  try {
    // First get all save slots
    const slotsResult = await loadSaveSlots();
    
    if (!slotsResult.success) {
      throw new Error('Failed to load save slots');
    }
    
    // Find the active slot
    const activeSlot = slotsResult.slotData.save_slots.find(slot => slot.active && slot.settings);
    
    if (activeSlot) {
      // Save active settings to localStorage for compatibility
      localStorage.setItem('settings', JSON.stringify(activeSlot.settings));
      
      return {
        success: true,
        settings: activeSlot.settings,
        source: 'cloud-slot',
        slotData: slotsResult.slotData,
        activeSlot: activeSlot.slot
      };
    }
    
    // No active slot with settings found, try legacy local storage
    const localSettings = localStorage.getItem('settings');
    if (localSettings) {
      try {
        const parsedSettings = JSON.parse(localSettings);
        return {
          success: true,
          settings: parsedSettings,
          source: 'local',
          slotData: slotsResult.slotData
        };
      } catch (e) {
        if (DEBUG) {
          console.error('Error parsing local settings:', e);
        }
      }
    }
    
    // If nothing found, return defaults
    return {
      success: true,
      settings: defaultSettings,
      source: 'default',
      slotData: slotsResult.slotData
    };
  } catch (error) {
    if (DEBUG) {
      console.error('Error loading user settings:', error);
    }
    
    // Try local storage as fallback
    try {
      const localSettings = localStorage.getItem('settings');
      if (localSettings) {
        return {
          success: true,
          settings: JSON.parse(localSettings),
          source: 'local-fallback'
        };
      }
    } catch (localError) {
      if (DEBUG) {
        console.error('Error loading from localStorage:', localError);
      }
    }
    
    // Return defaults if all else fails
    return {
      success: false,
      settings: defaultSettings,
      source: 'default-fallback',
      error
    };
  }
};

export const getUserEmail = async () => {
  try {
    const user = await getCurrentUser();
    const userAttributes = await fetchUserAttributes();    
    if (userAttributes && userAttributes.email) {
      // Store email for future fallback
      localStorage.setItem('userEmail', userAttributes.email);
      
      return {
        success: true,
        userEmail: userAttributes.email
      };
    } else {
      if (DEBUG) {
        console.error("No email found in user attributes");
      }
      
      const fallbackEmail = localStorage.getItem('userEmail');
      if (fallbackEmail) {
        return {
          success: true,
          userEmail: fallbackEmail,
          source: 'fallback'
        };
      }
      
      return {
        success: false,
        message: 'Email not found in user attributes'
      };
    }
  } catch (error) {
    if (DEBUG) {
      console.error('Error retrieving user email:', error);
    }
    
    const fallbackEmail = localStorage.getItem('userEmail');
    if (fallbackEmail) {
      return {
        success: true,
        userEmail: fallbackEmail,
        source: 'fallback-error'
      };
    }
    
    return {
      success: false,
      error,
      message: 'Error retrieving user email'
    };
  }
};